import React, { useState, useEffect } from 'react';
import { supabase, updateMatchmakingPreferences } from '../supabaseClient';

const Matchmaking = ({ userId }) => {
  const [preferences, setPreferences] = useState({
    game_types: [],
    skill_level: 'beginner',
    preferred_times: [],
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
  });
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedGame, setSelectedGame] = useState('');

  useEffect(() => {
    loadPreferences();
    subscribeToMatches();
  }, [userId]);

  const loadPreferences = async () => {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('matchmaking_preferences')
        .eq('user_id', userId)
        .single();

      if (error) throw error;
      if (data?.matchmaking_preferences) {
        setPreferences(data.matchmaking_preferences);
      }
    } catch (error) {
      console.error('Error loading preferences:', error);
    }
  };

  const subscribeToMatches = () => {
    if (!selectedGame) return;

    const subscription = supabase
      .channel('public:user_profiles')
      .on('postgres_changes', { 
        event: '*', 
        schema: 'public',
        table: 'user_profiles'
      }, payload => {
        if (payload.new && 
            payload.new.user_id !== userId && 
            payload.new.matchmaking_preferences?.game_types?.includes(selectedGame)) {
          updateMatches(payload.new);
        }
      })
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  };

  const updateMatches = async (newMatch) => {
    // Filter matches based on preferences
    if (
      newMatch.matchmaking_preferences.skill_level === preferences.skill_level &&
      newMatch.matchmaking_preferences.game_types.some(game => 
        preferences.game_types.includes(game)
      )
    ) {
      setMatches(prev => [...prev, newMatch]);
    }
  };

  const findMatches = async () => {
    if (!selectedGame) {
      alert('Please enter a game name first!');
      return;
    }

    setLoading(true);
    try {
      // First update current user's preferences to include the selected game
      const updatedPreferences = {
        ...preferences,
        game_types: [...new Set([...preferences.game_types, selectedGame])]
      };
      
      await updateMatchmakingPreferences(userId, updatedPreferences);
      setPreferences(updatedPreferences);

      console.log('Searching for matches with game:', selectedGame);

      // Get all profiles except current user
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .neq('user_id', userId);

      if (error) {
        console.error('Database query error:', error);
        throw error;
      }

      console.log('Found profiles:', data);

      // Filter matches based on game and preferences
      const filteredMatches = data?.filter(match => {
        const matchPrefs = match.matchmaking_preferences;
        console.log('Checking match:', match.email, 'preferences:', matchPrefs);
        
        // Case-insensitive game name comparison
        const hasMatchingGame = matchPrefs?.game_types?.some(game => 
          game.toLowerCase() === selectedGame.toLowerCase()
        );

        const matchesSkillLevel = !preferences.skill_level || 
          matchPrefs?.skill_level === preferences.skill_level;

        const matchesTime = !preferences.preferred_times.length || 
          matchPrefs?.preferred_times?.some(time => 
            preferences.preferred_times.includes(time)
          );

        const isMatch = hasMatchingGame && matchesSkillLevel && matchesTime;
        console.log('Match result:', {
          hasMatchingGame,
          matchesSkillLevel,
          matchesTime,
          isMatch
        });

        return isMatch;
      }) || [];

      console.log('Filtered matches:', filteredMatches);
      setMatches(filteredMatches);

      if (filteredMatches.length === 0) {
        alert('No matches found. Try adjusting your preferences or check back later!');
      } else {
        alert(`Found ${filteredMatches.length} potential gaming partners!`);
      }
    } catch (error) {
      console.error('Error finding matches:', error);
      alert('Error finding matches. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const savePreferences = async () => {
    try {
      await updateMatchmakingPreferences(userId, preferences);
      alert('Preferences saved!');
    } catch (error) {
      console.error('Error saving preferences:', error);
    }
  };

  return (
    <div className="space-y-6 p-4 bg-xkid-gray rounded-lg border border-xkid-green">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-xkid-green mb-4">Find Gaming Partners</h2>
        <p className="text-gray-400">Match with players who share your gaming preferences!</p>
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-xkid-green mb-2">Game</label>
          <input
            type="text"
            value={selectedGame}
            onChange={(e) => setSelectedGame(e.target.value)}
            placeholder="Enter game name..."
            className="w-full p-3 rounded-lg bg-xkid-gray-light text-white border border-xkid-green focus:outline-none focus:ring-2 focus:ring-xkid-green"
          />
        </div>

        <div>
          <label className="block text-xkid-green mb-2">Skill Level</label>
          <select
            value={preferences.skill_level}
            onChange={(e) => setPreferences(prev => ({ ...prev, skill_level: e.target.value }))}
            className="w-full p-3 rounded-lg bg-xkid-gray-light text-white border border-xkid-green focus:outline-none focus:ring-2 focus:ring-xkid-green"
          >
            <option value="beginner">Beginner</option>
            <option value="intermediate">Intermediate</option>
            <option value="advanced">Advanced</option>
            <option value="expert">Expert</option>
          </select>
        </div>

        <div>
          <label className="block text-xkid-green mb-2">Preferred Gaming Times</label>
          <select
            multiple
            value={preferences.preferred_times}
            onChange={(e) => setPreferences(prev => ({
              ...prev,
              preferred_times: Array.from(e.target.selectedOptions, option => option.value)
            }))}
            className="w-full p-3 rounded-lg bg-xkid-gray-light text-white border border-xkid-green focus:outline-none focus:ring-2 focus:ring-xkid-green"
          >
            <option value="morning">Morning (6AM-12PM)</option>
            <option value="afternoon">Afternoon (12PM-6PM)</option>
            <option value="evening">Evening (6PM-12AM)</option>
            <option value="night">Night (12AM-6AM)</option>
          </select>
        </div>

        <div className="flex space-x-4">
          <button
            onClick={savePreferences}
            className="flex-1 p-3 rounded-lg bg-xkid-green text-black font-bold hover:bg-xkid-green-dark transition-colors"
          >
            Save Preferences
          </button>
          <button
            onClick={findMatches}
            disabled={loading}
            className="flex-1 p-3 rounded-lg bg-xkid-red text-white font-bold hover:bg-xkid-red-dark transition-colors disabled:opacity-50"
          >
            {loading ? 'Searching...' : 'Find Matches'}
          </button>
        </div>
      </div>

      <div className="space-y-4 mt-8">
        <h3 className="text-xl font-bold text-xkid-green">Potential Matches</h3>
        {matches.map((match) => (
          <div
            key={match.id}
            className="p-4 rounded-lg border border-xkid-green bg-xkid-gray-light"
          >
            <div className="flex justify-between items-start">
              <div>
                <h4 className="font-bold text-xkid-green">{match.email}</h4>
                <p className="text-gray-300 mt-2">
                  Skill Level: {match.matchmaking_preferences.skill_level}
                </p>
                <p className="text-gray-300">
                  Games: {match.matchmaking_preferences.game_types.join(', ')}
                </p>
                <p className="text-gray-300">
                  Times: {match.matchmaking_preferences.preferred_times.join(', ')}
                </p>
              </div>
              <button
                onClick={() => window.location.href = `mailto:${match.email}`}
                className="px-3 py-1 rounded bg-xkid-green text-black text-sm hover:bg-xkid-green-dark transition-colors"
              >
                Contact
              </button>
            </div>
          </div>
        ))}
        {matches.length === 0 && (
          <p className="text-center text-gray-400">No matches found yet. Try adjusting your preferences!</p>
        )}
      </div>
    </div>
  );
};

export default Matchmaking; 